/**
 * Tenant API. `GET /tenant/` is public and tells the app which institute it
 * is running for (name, logo, colours, feature flags, status). Contract:
 * `tutorLoop-docs/14-API-CONTRACT.md` section 1. Wire fields are snake_case;
 * everything the app reads is camelCase.
 */

import { API_BASE, resolveMediaUrl } from "@/lib/tenant/basePath";

import { ApiError } from "./errors";


export const FEATURE_NAMES = [
  "ai_assistant",
  "google_workspace",
  "parent_payments",
  "custom_domain",
  "careers",
  "ads_conversions",
  "loopdesk_support",
] as const;

export type FeatureName = (typeof FEATURE_NAMES)[number];

export type TenantStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "suspended"
  | "cancelled";

export interface TenantWire {
  schema: string;
  slug: string;
  name: string;
  display_name: string;
  status: TenantStatus;
  is_internal: boolean;
  logo_url: string | null;
  logo_mark_url: string | null;
  /** `""` on the institute's own domain, `"/<slug>"` on the TutorLoop path host. */
  base_path?: string;
  primary_colour: string | null;
  accent_colour: string | null;
  support_email: string | null;
  features: Partial<Record<FeatureName, boolean>>;
  login_options?: {
    magic_link?: boolean;
    username_login?: boolean;
    signup?: boolean;
  };
  plan: { code: string; name: string } | null;
  setup_complete: boolean;
  /** Only on the branding endpoints. */
  email_sender_name?: string;
  reply_to_email?: string;
}

export interface Tenant {
  schema: string;
  slug: string;
  /** Legal or short name. Prefer `displayName` for anything people read. */
  name: string;
  /** The name shown across the app, emails and the document title. */
  displayName: string;
  status: TenantStatus;
  /** An internal tenant (DrTutor) is not billed through TutorLoop. */
  isInternal: boolean;
  logoUrl: string | null;
  logoMarkUrl: string | null;
  basePath: string;
  primaryColour: string | null;
  accentColour: string | null;
  supportEmail: string | null;
  features: Record<FeatureName, boolean>;
  loginOptions: { magicLink: boolean; usernameLogin: boolean; signup: boolean };
  plan: { code: string; name: string } | null;
  setupComplete: boolean;
  emailSenderName: string | null;
  replyToEmail: string | null;
}

export function mapTenant(raw: TenantWire): Tenant {
  const features = {} as Record<FeatureName, boolean>;
  for (const name of FEATURE_NAMES) features[name] = raw.features?.[name] === true;
  return {
    schema: raw.schema,
    slug: raw.slug,
    name: raw.name,
    displayName: raw.display_name || raw.name,
    status: raw.status,
    isInternal: raw.is_internal,
    logoUrl: resolveMediaUrl(raw.logo_url ?? null),
    basePath: raw.base_path ?? "",
    logoMarkUrl: resolveMediaUrl(raw.logo_mark_url ?? null),
    primaryColour: raw.primary_colour ?? null,
    accentColour: raw.accent_colour ?? null,
    supportEmail: raw.support_email ?? null,
    features,
    loginOptions: {
      magicLink: raw.login_options?.magic_link ?? true,
      usernameLogin: raw.login_options?.username_login ?? true,
      signup: raw.login_options?.signup ?? false,
    },
    plan: raw.plan ?? null,
    setupComplete: raw.setup_complete,
    emailSenderName: raw.email_sender_name ?? null,
    replyToEmail: raw.reply_to_email ?? null,
  };
}

export const tenantApi = {
  /**
   * Fetched without credentials on purpose: it is public, and a stale token
   * from another institute must not turn it into a 401.
   */
  async get(signal?: AbortSignal): Promise<Tenant> {
    const res = await fetch(`${API_BASE}/tenant/`, {
      headers: { Accept: "application/json" },
      signal,
    });
    const ct = res.headers.get("Content-Type") ?? "";
    const body: unknown = ct.includes("application/json") ? await res.json() : null;
    if (!res.ok) {
      const b = (body ?? {}) as { code?: string; detail?: string };
      throw new ApiError(b.code || `http_${res.status}`, b.detail || `HTTP ${res.status}`, res.status);
    }
    return mapTenant(body as TenantWire);
  },
};
